import { useTranslation } from "react-i18next";
import Modal from "../ui/Modal";
import Button from "../ui/Button";

const AGREEMENT_FALLBACKS = {
  "term-cond-title": "Terms and conditions",
  "term-cond-h": "ELECTRONIC RECORD AND SIGNATURE DISCLOSURE",
  "term-cond-p1":
    "This Electronic Record and Signature Disclosure ('Disclosure') is an agreement between the Document Creator ('Sender') and the Signer ('You'), facilitated through the {{appName}} platform ('Platform'). By signing documents via {{appName}}, you agree to the terms outlined in this Disclosure. Please read it carefully before proceeding.",
  "term-cond-h1": "1. Purpose and Scope",
  "term-cond-p2":
    "This Disclosure governs the use of electronic records and signatures for documents sent by the Sender and signed by You through the {{appName}} platform. By agreeing, You acknowledge that:",
  "term-cond-p3": "You will receive and sign documents electronically via {{appName}}.",
  "term-cond-p4": "Your electronic signature is legally binding and equivalent to a handwritten signature.",
  "term-cond-h2": "2. Consent to Use Electronic Records and Signatures",
  "term-cond-p5": "By agreeing to this Disclosure:",
  "term-cond-p6":
    "You consent to transact electronically with the Sender using {{appName}} and understand that this consent is valid until withdrawn.",
  "term-cond-p7": "You agree to review, sign, and return documents electronically using {{appName}}.",
  "term-cond-p8": "This consent applies only to the specific document(s) provided by the Sender.",
  "term-cond-h3": "3. Right to Withdraw Consent",
  "term-cond-p9": "You have the right to withdraw your consent to use electronic records and signatures by:",
  "term-cond-p10":
    "Contacting the Sender directly using the contact information provided in their communication.",
  "term-cond-p11": "Notifying the Sender before completing the signing process.",
  "term-cond-h4": "4. Hardware and Software Requirements",
  "term-cond-p12": "To sign documents using {{appName}}, You must have:",
  "term-cond-p13": "A device with internet access.",
  "term-cond-p14": "A compatible web browser with JavaScript enabled.",
  "term-cond-p15": "An email account to receive notifications and signed documents.",
  "term-cond-p16": "If these requirements change, the Sender or {{appName}} will notify You of the updates.",
  "term-cond-h5": "5. Retention of Records and Request for Paper Copies",
  "term-cond-p17":
    "You are responsible for ensuring You can access and retain the electronically signed document(s). {{appName}} provides tools to download or print the signed document immediately after the signing process is complete.",
  "term-cond-p18":
    "If You prefer a paper copy of any electronically signed document, You may request it directly from the Sender. The Sender may charge a reasonable fee for providing paper copies unless prohibited by applicable law.",
  "term-cond-h6": "6. Acknowledgment and Agreement",
  "term-cond-p19": "By proceeding, You:",
  "term-cond-p20": "Confirm that You can access and review electronic documents as described in this Disclosure.",
  "term-cond-p21":
    "Agree to use electronic records and signatures for the specified transaction(s) with the Sender.",
  "term-cond-p22":
    "Understand that {{appName}} is a platform facilitating the transaction and is not a party to the agreement.",
  "term-cond-h7": "7. Legal Effect",
  "term-cond-p23": "Your electronic signature facilitated through {{appName}}:",
  "term-cond-p24":
    "Complies with applicable electronic signature laws, including but not limited to the E-SIGN Act in the United States, the EU eIDAS Regulation, and India's Information Technology Act.",
  "term-cond-p25": "Is legally binding between You and the Sender for the signed document(s).",
  "term-cond-h8": "8. Platform Role and Limitation of Liability",
  "term-cond-p26":
    "{{appName}} serves as a platform to facilitate electronic transactions. It is not responsible for the content, validity, or enforceability of the documents sent by the Sender. Any disputes or issues related to the document or its signing must be resolved directly between You and the Sender.",
  "term-cond-h9": "9. Termination of Agreement",
  "term-cond-p27":
    "Your consent applies only to the current transaction unless otherwise specified by the Sender. {{appName}} reserves the right to terminate access to its platform for violations of its Terms of Service or policies.",
  "term-cond-p28":
    "By selecting 'I Agree,' You confirm that You understand and agree to the terms of this Electronic Record and Signature Disclosure and consent to electronically sign the document(s) provided by the Sender through {{appName}}.",
  "term-cond-p29":
    "If You have questions about this Disclosure, contact the Sender directly. For technical support with {{appName}}, visit ",
  "term-cond-p30": " or email",
  "agrre-button": "Agree & Continue",
  close: "Close"
};

function AgreementContent({ isOpen, onClose, onAccept }) {
  const { t } = useTranslation();
  const appName = "DANAsign";
  const headingClassName = "text-base font-semibold text-app-ink";
  const listClassName = "list-disc pl-5 space-y-1";
  const tr = (key, options = {}) =>
    t(key, {
      defaultValue: AGREEMENT_FALLBACKS[key] || key,
      ...options
    });

  return (
    <Modal
      isOpen={isOpen}
      title={tr("term-cond-title")}
      onClose={onClose}
      panelClassName="max-w-3xl"
      bodyClassName="max-h-[72vh] overflow-y-auto pr-1"
    >
      <div className="space-y-4 text-sm text-app-ink-muted">
        <div>
          <h3 className={headingClassName}>{tr("term-cond-h")}</h3>
          <p className="mt-2">{tr("term-cond-p1", { appName })}</p>
        </div>

        <div>
          <h3 className={headingClassName}>{tr("term-cond-h1")}</h3>
          <p className="mt-2">{tr("term-cond-p2", { appName })}</p>
          <ul className={`mt-2 ${listClassName}`}>
            <li>{tr("term-cond-p3", { appName })}</li>
            <li>{tr("term-cond-p4")}</li>
          </ul>
        </div>

        <div>
          <h3 className={headingClassName}>{tr("term-cond-h2")}</h3>
          <p className="mt-2">{tr("term-cond-p5")}</p>
          <ul className={`mt-2 ${listClassName}`}>
            <li>{tr("term-cond-p6", { appName })}</li>
            <li>{tr("term-cond-p7", { appName })}</li>
            <li>{tr("term-cond-p8")}</li>
          </ul>
        </div>

        <div>
          <h3 className={headingClassName}>{tr("term-cond-h3")}</h3>
          <p className="mt-2">{tr("term-cond-p9")}</p>
          <ul className={`mt-2 ${listClassName}`}>
            <li>{tr("term-cond-p10")}</li>
            <li>{tr("term-cond-p11")}</li>
          </ul>
        </div>

        <div>
          <h3 className={headingClassName}>{tr("term-cond-h4")}</h3>
          <p className="mt-2">{tr("term-cond-p12", { appName })}</p>
          <ul className={`mt-2 ${listClassName}`}>
            <li>{tr("term-cond-p13")}</li>
            <li>{tr("term-cond-p14")}</li>
            <li>{tr("term-cond-p15")}</li>
          </ul>
          <p className="mt-2">{tr("term-cond-p16", { appName })}</p>
        </div>

        <div>
          <h3 className={headingClassName}>{tr("term-cond-h5")}</h3>
          <p className="mt-2">{tr("term-cond-p17", { appName })}</p>
          <p className="mt-2">{tr("term-cond-p18")}</p>
        </div>

        <div>
          <h3 className={headingClassName}>{tr("term-cond-h6")}</h3>
          <p className="mt-2">{tr("term-cond-p19")}</p>
          <ul className={`mt-2 ${listClassName}`}>
            <li>{tr("term-cond-p20")}</li>
            <li>{tr("term-cond-p21")}</li>
            <li>{tr("term-cond-p22", { appName })}</li>
          </ul>
        </div>

        <div>
          <h3 className={headingClassName}>{tr("term-cond-h7")}</h3>
          <p className="mt-2">{tr("term-cond-p23", { appName })}</p>
          <ul className={`mt-2 ${listClassName}`}>
            <li>{tr("term-cond-p24")}</li>
            <li>{tr("term-cond-p25")}</li>
          </ul>
        </div>

        <div>
          <h3 className={headingClassName}>{tr("term-cond-h8")}</h3>
          <p className="mt-2">{tr("term-cond-p26", { appName })}</p>
        </div>

        <div>
          <h3 className={headingClassName}>{tr("term-cond-h9")}</h3>
          <p className="mt-2">{tr("term-cond-p27", { appName })}</p>
        </div>

        <div>
          <p className="font-semibold text-app-ink">{tr("term-cond-p28", { appName })}</p>
          <p className="mt-2">
            {tr("term-cond-p29", { appName })}
            <a
              href="https://www.danaconnect.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-app-brand hover:underline"
            >
              www.danaconnect.com
            </a>
            {tr("term-cond-p30")}
            <span className="font-semibold text-app-ink"> support@danaconnect.com</span>
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-app-line pt-4">
          <Button variant="ghost" onClick={onClose}>
            {tr("close")}
          </Button>
          <Button onClick={onAccept}>{tr("agrre-button")}</Button>
        </div>
      </div>
    </Modal>
  );
}

export default AgreementContent;
